import { UpdateCompanyClientData } from '@esg/syntiro-api-core';
import { Grid, Typography, TextField } from '@mui/material';
import React from 'react';
import { MuiFileInput } from 'mui-file-input';
import { Company } from '@esg/esg-global-types';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { GCP_FILE_UPLOAD_BUCKET, API_ENDPOINT } from '../../../env';
import { generateFileUploadRequest } from '../../../lib/file_upload/uploadRequestPrep';
import axios, { AxiosResponse } from 'axios';
import { hasBannedCharacters } from '../../../lib/validation/text_validation';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { CompanyContext } from '../../../context/CompanyContext';
import { GroupContext } from '../../../context/GroupContext';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { updateCompany } from '../../../graphql/services/company.service';
import { getAuthHeaderForUser, handleAuthError } from '../../../lib/auth/auth.util';

const PanelPersonalisation = () => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);
  const [logoFile, setLogoFile] = React.useState<File | null>(null);
  const [companyName, setCompanyName] = React.useState<string>('');
  const [updateLoading, setUpdateLoading] = React.useState<boolean>(false);

  const invalidCompanyName = companyName
    ? hasBannedCharacters(companyName)
    : companyName.length > 1
      ? false
      : true;

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCompanyName(event.target.value);
  };

  const handleFileChange = (new_file: File | null) => {
    setLogoFile(new_file);
  };

  const handleCompanyDetailsUpdate = async () => {
    setUpdateLoading(true);
    try {
      if (group && company) {
        const new_company_object: Company = {
          id: company.id,
          deleted: null,
          name: companyName,
          active: true,
          configuration: {
            ...company.configuration
          },
          subscription: company.subscription,
          client_bigquery_dataset: company.client_bigquery_dataset,
          client_bigquery_dataset_sync: company.client_bigquery_dataset_sync
        };
        const file_path = `${group.id}/${company.id}/${'configuration'}`;
        if (logoFile) {
          const file_name = `logo-${company.name.toLowerCase().replace(/\s/g, '_')}`;
          const file_upload = generateFileUploadRequest(
            logoFile,
            file_name,
            atob(GCP_FILE_UPLOAD_BUCKET),
            file_path,
            'true',
            'true'
          );

          await axios({
            method: 'post',
            url: `${API_ENDPOINT}/upload_file`,
            data: file_upload,
            headers: {
              ...(await getAuthHeaderForUser())
            }
          })
            .then((uploaded_file: AxiosResponse) => {
              new_company_object.configuration.logo = uploaded_file.data.url;
            })
            .catch((err: unknown) => {
              const tracking_id: string = generateUuidV4();
              log(
                'error',
                new MetadataError(
                  err instanceof Error
                    ? err.message
                    : 'Error: PanelPersonalisation failed on an unknown error while calling handleCompanyDetailsUpdate.',
                  {
                    new_company_object: new_company_object,
                    file_upload: file_upload
                  },
                  tracking_id
                )
              );
              setFeedbackData({
                message: `Unable to upload logo. Tracking ID: ${tracking_id}`,
                state: true,
                type: 'error'
              });
              handleAuthError(err);
            });
        }
        const update_company_client_data: UpdateCompanyClientData = {
          ...new_company_object
        };
        await updateCompany(
          {
            group_id: group.id
          },
          update_company_client_data
        ).catch((error) => {
          throw new Error(error);
        });
        setFeedbackData({
          message: `${companyName} updated, please refresh page for changes to take effect`,
          state: true,
          type: 'success'
        });
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelPersonalisation failed on an unknown error while calling handleCompanyDetailsUpdate.',
          {
            company: company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to update company. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setUpdateLoading(false);
      setLogoFile(null);
    }
  };

  React.useEffect(() => {
    try {
      if (group && company) {
        setCompanyName(company.name);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelPersonalisation failed on an unknown error while initialising.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `An error occurred. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  }, [group, company]);

  return (
    <Grid container spacing={4}>
      <Grid size={12}>
        <Typography align="left" paddingBottom={'10px'}>
          Name
        </Typography>
        <TextField
          error={invalidCompanyName}
          id="standard-basic"
          variant="standard"
          value={companyName}
          onChange={handleNameChange}
          fullWidth
        />
      </Grid>
      <Grid size={4}>
        <Typography color="inherit" align="left" paddingBottom={'10px'} component="div">
          Logo
        </Typography>
        <MuiFileInput
          label={'Logo'}
          value={logoFile}
          fullWidth={true}
          onChange={handleFileChange}
        />
      </Grid>
      <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <PrimaryButton
          disabled={invalidCompanyName}
          loading={updateLoading}
          onClick={handleCompanyDetailsUpdate}
          sx={{ minWidth: '150px' }}
        >
          Update
        </PrimaryButton>
      </Grid>
    </Grid>
  );
};

export default PanelPersonalisation;
